<template>
  <div ref="containerRef" class="reviews py-16 !m-0" role="region" :aria-label="topText">
    <div class="reviews-inner relative">
      <div class="title" v-if="topText || headlineText">
        <span class="sub-heading text-base 3xl:text-md font-marcellus text-purple" v-if="topText">{{ topText }}</span>
        <h2 class="heading text-2xl lg:text-6xl 3xl:text-10xl font-marcellus text-grey-700 leading-[30px] md:leading-none" v-if="headlineText">{{ headlineText }}</h2>
      </div>
      <div class="content mt-7 lg:mt-10">
        <!-- Reviews Grid -->
        <div
          class="flex flex-nowrap items-stretch md:items-start md:flex-wrap hide-scrollbar -mx-5 px-5 md:px-0 md:mx-0 md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 gap-y-12 overflow-x-scroll md:overflow-x-hidden snap-x snap-mandatory">
          <!-- Review Card 1 -->
          <div
            class="review-card bg-white border border-grey-100 rounded-[15px] md:rounded-[20px] p-4 md:p-7 shadow-sm min-w-[90%] md:min-w-[0px] md:h-full h-auto snap-center flex flex-col justify-between"
            v-for="(review, index) in Reviews"
            :key="index"
          >
            <template v-if="review.boxReviews?.product_review?.product">
              <div class="flex items-start gap-4 mb-4 xl:mb-6 ">
                <div class="review-image aspect-square w-[65px] h-[65px] xl:w-[62px] xl:h-[62px] 2xl:w-[94px] 2xl:h-[94px] border border-grey-100 rounded-lg bg-grey-100 flex items-center justify-center">
                  <NuxtLink :to="formatLink(getProductRoute(review.boxReviews.product_review.product))">
                    <SharedImage
                      :src="review.boxReviews.product_review.product.cover?.media?.url"
                      :alt="review.boxReviews.product_review.product.cover?.media?.alt ? review.boxReviews.product_review.product.cover?.media?.alt : $t('mysofbed')"
                      imgClass="w-[65px] h-[65px] xl:w-[62px] xl:h-[62px] 2xl:w-[94px] 2xl:h-[94px] object-contain rounded-lg aspect-square mix-blend-multiply"
                      loading="lazy"
                      width="65"
                      height="65"
                      :mimeType="review.boxReviews.product_review.product.cover?.media?.mimeType"
                      :sizes="{ desktop: '154x65', tablet: '65x65', mobile: '65x65' }"
                    />
                  </NuxtLink>
                </div>
                <div class="flex flex-col">
                  <div class="flex flex-col 3xl:flex-row justify-between w-full gap-1">
                    <div class="block w-full">
                      <h3 class="font-medium text-grey-700 text-lg 3xl:text-xl leading-5 line-clamp-1" v-if="review.boxReviews.product_review.customer?.firstName">{{ review.boxReviews.product_review.customer.firstName + ' ' + review.boxReviews.product_review.customer.lastName }}</h3>
                      <h3 class="font-medium text-grey-700 text-lg 3xl:text-xl leading-5 line-clamp-1" v-else-if="review.boxReviews.product_review?.externalUser">{{ review.boxReviews.product_review.externalUser }}</h3>
                      
                      <p class="text-base 3xl:text-md text-grey-600 leading-none mt-2" v-if="review.boxReviews.product_review.createdAt">{{ formatDate(review.boxReviews.product_review.createdAt) }}</p>
                      <!-- <h4
                        v-if="review.boxReviews.product_review.product.translated.name"
                        class="text-base 3xl:text-lg text-primary font-mulish mt-4 3xl:mt-5">
                        {{ review.boxReviews.product_review.product.translated.name }}
                      </h4> -->
                    </div>
                    <div
                      class="block xs:flex md:flex lg:block flex-wrap xl:flex 3xl:block min-w-[150px] text-left md:text-right lg:text-left xl:text-right mt-2 md:mt-0">
                      <div 
                        v-if="review.boxReviews.product_review.points"
                        class="rating-stars flex items-center justify-start xl:justify-end gap-x-1 sm:gap-x-[4px] text-rating-yellow relative mr-2 xl:mr-0"
                        aria-hidden="true">
                        <i v-for="n in review.boxReviews.product_review.points" :key="n" class="icon-star-sharp inline-block w-[13px] h-[13px] sm:w-[16px] sm:h-[16px]"></i>
                      </div>
                      <span class="text-sm text-[#7B7479] ml-0 sm:ml-2 mt-0 lg:ml-0 xl:ml-2 md:mt-0"
                      v-if="review.boxReviews.product_review.createdAt"
                        >{{ timeAgo(review.boxReviews.product_review.createdAt, locale) }}</span
                      >
                    </div>
                  </div>
                  <div class="hidden 2xl:block w-full flex-1">
                    <NuxtLink :to="formatLink(getProductRoute(review.boxReviews.product_review.product))">
                      <h4
                        v-if="review.boxReviews.product_review.product.translated.name"
                        class="text-base 3xl:text-lg text-primary font-mulish mt-4 3xl:mt-5 line-clamp-3">
                        {{ review.boxReviews.product_review.product.translated.name }}
                      </h4>
                    </NuxtLink>
                  </div>
                </div>
                
              </div>
              <div v-if="review.boxReviews?.product_review?.product" class="block 2xl:hidden w-full mb-4 xl:mb-6 flex-1">
                <h4
                  v-if="review.boxReviews.product_review.product.translated.name"
                  class="text-base 3xl:text-lg text-primary font-mulish mt-0 3xl:mt-5 line-clamp-3">
                  {{ review.boxReviews.product_review.product.translated.name }}
                </h4>
              </div>
              <div
              v-if="review.boxReviews.product_review.content"
              class="review-message border-t border-grey-100 pt-4">
                <p class="text-grey-600 line-clamp-3 min-h-[70px]">
                  {{ review.boxReviews.product_review.content }}
                </p>
              </div>
            </template>
          </div>
        </div>
        <div class="progress-bar block md:hidden mt-8 h-[6px] bg-gray-300 rounded-full mx-10">
          <div class="progress-bar-inner h-[6px] bg-primary transition-all ease-linear rounded-full w-[0%]"></div>
        </div>
      </div>
      <div class="btn-wrapper mt-10 md:mt-0 md:absolute md:top-0 md:right-0 text-center" v-if="boxLink">
        <SharedButton 
          :button="boxLink"
          :isInnerHtml="true"
          class="btn btn-primary btn-icon-end"
        >
          <span class="my-auto"> {{ boxLink.title }}</span>
          <i class="icon-arrow-right font-bold" aria-hidden="true"></i>
        </SharedButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CmsElementReview } from "~/composables/useCustom";
import { useI18n } from "vue-i18n";
import { useProgressBar } from '@/composables/useProgressBar';
import SharedImage from "~/components/shared/SharedImage.vue";
import {
  getProductRoute,
} from "@shopware/helpers";
const containerRef = ref<HTMLElement | null>(null);
useProgressBar(containerRef);
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const props = defineProps<{
  content: CmsElementReview;
}>();

const { topText, headlineText, Reviews, boxLink } = props.content.data;

const formatDate = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("T")[0].split("-");
  return `${day}.${month}.${year}`;
};

const { locale } = useI18n();

const timeAgo = (dateString, lang = "en") => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date; // Difference in milliseconds

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });

  if (years > 0) return rtf.format(-years, "year");
  if (months > 0) return rtf.format(-months, "month");
  if (days > 0) return rtf.format(-days, "day");
  if (hours > 0) return rtf.format(-hours, "hour");
  if (minutes > 0) return rtf.format(-minutes, "minute");
  return rtf.format(-seconds, "second");
};

</script>